import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  germinationChambersApi,
  type GerminationChamber,
  type CreateGerminationChamberRequest,
} from "../services/germinationChambersApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";

const QUERY_KEY = ["germination-chambers"];

const useAllGerminationChambers = () =>
  useQuery({ queryKey: QUERY_KEY, queryFn: germinationChambersApi.getAll });

const useCreateChamber = () =>
  useMutation({
    mutationFn: germinationChambersApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

const useUpdateChamber = () =>
  useMutation({
    mutationFn: germinationChambersApi.update,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

const useDeleteChamber = () =>
  useMutation({
    mutationFn: germinationChambersApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

// ─── Chamber Form Dialog ─────────────────────────────────────────────────────

interface ChamberFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: GerminationChamber | null;
}

const ChamberFormDialog = ({ open, onOpenChange, editing }: ChamberFormDialogProps) => {
  const notify = useNotification();
  const createMutation = useCreateChamber();
  const updateMutation = useUpdateChamber();

  const [form, setForm] = useState<CreateGerminationChamberRequest & { isActive?: boolean }>({
    chamberName: editing?.chamberName ?? "",
    chamberCode: editing?.chamberCode ?? "",
    description: editing?.description ?? "",
    maxCapacity: editing?.maxCapacity,
    isActive: editing?.isActive ?? true,
  });

  const handleSubmit = () => {
    if (!form.chamberName || !form.chamberCode) {
      notify.error("Chamber name and code are required");
      return;
    }
    const isPending = createMutation.isPending || updateMutation.isPending;
    if (isPending) return;

    const payload = {
      chamberName: form.chamberName,
      chamberCode: form.chamberCode,
      description: form.description,
      maxCapacity: form.maxCapacity,
    };

    if (editing) {
      updateMutation.mutate(
        { ...payload, id: editing.id, isActive: form.isActive ?? true },
        {
          onSuccess: () => {
            notify.success("Chamber updated");
            onOpenChange(false);
          },
          onError: (err: unknown) =>
            notify.error(getErrorMessage(err) || "Failed to update"),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          notify.success("Chamber created");
          onOpenChange(false);
        },
        onError: (err: unknown) =>
          notify.error(getErrorMessage(err) || "Failed to create"),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Chamber" : "New Germination Chamber"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Chamber Name *</FieldLabel>
              <Input
                value={form.chamberName}
                onChange={(e) => setForm((f) => ({ ...f, chamberName: e.target.value }))}
                placeholder="e.g. Chamber A"
              />
            </Field>
            <Field>
              <FieldLabel>Chamber Code *</FieldLabel>
              <Input
                value={form.chamberCode}
                onChange={(e) => setForm((f) => ({ ...f, chamberCode: e.target.value }))}
                placeholder="e.g. GC-A1"
              />
            </Field>
          </div>

          <Field>
            <FieldLabel>Max Capacity (trays)</FieldLabel>
            <Input
              type="number"
              value={form.maxCapacity ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, maxCapacity: e.target.value ? parseInt(e.target.value) : undefined }))
              }
              placeholder="Optional"
            />
          </Field>

          <Field>
            <FieldLabel>Description</FieldLabel>
            <Textarea
              value={form.description ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

export const GerminationChambersPage = () => {
  const notify = useNotification();
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission("greenhouse_stock.manage");
  const chambersQuery = useAllGerminationChambers();
  const deleteMutation = useDeleteChamber();

  const [formOpen, setFormOpen] = useState(false);
  const [editingChamber, setEditingChamber] = useState<GerminationChamber | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GerminationChamber | null>(null);

  const handleEdit = (chamber: GerminationChamber) => {
    setEditingChamber(chamber);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingChamber(null);
    setFormOpen(true);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        notify.success("Chamber deleted");
        setDeleteTarget(null);
      },
      onError: (err: unknown) =>
        notify.error(getErrorMessage(err) || "Failed to delete"),
    });
  };

  const chambers = chambersQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Germination Chambers</h1>
          <p className="text-muted-foreground mt-1">
            Manage germination chambers used for germinating sown trays.
          </p>
        </div>
        {canManage && (
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Chamber
          </Button>
        )}
      </div>

      {chambersQuery.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading chambers...</p>
        </div>
      )}

      {!chambersQuery.isLoading && chambers.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">No germination chambers configured yet</p>
          {canManage && (
            <Button variant="outline" className="mt-4" onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Chamber
            </Button>
          )}
        </div>
      )}

      {chambers.length > 0 && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Max Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chambers.map((chamber) => (
                <TableRow key={chamber.id}>
                  <TableCell className="font-medium">{chamber.chamberName}</TableCell>
                  <TableCell className="font-mono text-sm">{chamber.chamberCode}</TableCell>
                  <TableCell>{chamber.maxCapacity ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={chamber.isActive ? "default" : "secondary"}>
                      {chamber.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage ? (
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(chamber)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(chamber)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">View only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ChamberFormDialog
        key={`${editingChamber?.id ?? "new"}-${formOpen ? "open" : "closed"}`}
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingChamber(null); }}
        editing={editingChamber}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o: boolean) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Chamber</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.chamberName}</strong>?
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
